import { TextField, SelectField, SectionTitle } from "./FormBits";
import { VendorPicker, CchPicker } from "./MasterPickers";
import {
  QUARTERS,
  FUND_COMPONENTS,
  GRAIN_ITEMS,
  toNumber,
  ingredientAmount,
  displayIngredientName,
  money,
  qty,
  categoryMeals,
} from "@/lib/poshanConstants";
import { getFundOpening, getFundClosing, getGrainOpening, getGrainClosing } from "@/lib/poshanCompute";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, CalendarDays, Pencil, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function CardSection({ children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8"
    >
      {children}
    </section>
  );
}

export default function InputForms({
  schoolInfo,
  vendorInfo,
  months,
  rawMonths,
  dailyAggregateActive = false,
  cloudId,
  activeSchool,
  vendors = [],
  cchs = [],
  activeMonthIndex,
  setActiveMonthIndex,
  updateSchoolInfo,
  updateVendorInfo,
  applyVendorPick,
  handleQuarterChange,
  updateMeal,
  updateFund,
  updateGrain,
  updateIngredient,
  addIngredient,
  removeIngredient,
  updateCook,
  applyCookPick,
  addCook,
  removeCook,
  editIngredient,
  clearOverride,
  monthlySummaries,
}) {
  const activeMonth = months[activeMonthIndex];
  const activeMonthSummary = monthlySummaries[activeMonthIndex];
  const activeIsFromDaily = Boolean(activeMonth?._ingredientsFromDaily);
  const [editIngOpen, setEditIngOpen] = useState(false);
  const [editIngDraft, setEditIngDraft] = useState(null);
  const openEditIngredient = (ing) => {
    setEditIngDraft({ name: ing.name, quantity: ing.quantity, rate: ing.rate, unit: ing.unit });
    setEditIngOpen(true);
  };
  // Classwise meals: prefer daily-derived attendance sum; fall back to enrollment × mdm
  const dailyAtt = activeMonth?._attendanceSum;
  const classwiseMealsFromDaily = dailyAtt
    ? [
        { label: "Bal Vatika", meals: dailyAtt.balVatika || 0 },
        { label: "Primary", meals: dailyAtt.primary || 0 },
        { label: "Upper Primary", meals: dailyAtt.upperPrimary || 0 },
      ]
    : null;
  const expectedMeals =
    (activeMonth.meals.balVatikaEnrollment +
      activeMonth.meals.primaryEnrollment +
      activeMonth.meals.upperPrimaryEnrollment) *
    activeMonth.meals.mdmServedDays;
  const activeClassMeals = categoryMeals(activeMonth);

  return (
    <div className="space-y-8">
      {/* Step 1 - School Information */}
      <CardSection testId="section-school-info">
        <SectionTitle
          step="Step 1"
          title="School Information"
          note={activeSchool
            ? "Identity fields below are auto-filled from your active school profile. Edit them in Setup to keep all reports consistent."
            : "Saved automatically in this browser. Use 'Save to Cloud' above to back it up across devices."}
        />
        {activeSchool && (
          <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-moss/30 bg-moss/5 px-4 py-2.5 text-sm" data-testid="active-school-banner">
            <span className="rounded-full bg-moss/15 px-2.5 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-moss-dark">Active School</span>
            <span className="text-ink-900 font-semibold">{activeSchool.schoolName}</span>
            <span className="text-ink-700">· UDISE {activeSchool.udiseCode}</span>
            <Link to="/setup" data-testid="edit-in-setup-link" className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-clay underline">
              <Pencil className="h-3 w-3" /> Edit in Setup
            </Link>
          </div>
        )}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <TextField testId="input-school-name" label="School Name" required value={schoolInfo.schoolName} onChange={(v) => updateSchoolInfo("schoolName", v)} readOnly={!!activeSchool} />
          <TextField testId="input-udise" label="UDISE Code" required value={schoolInfo.udiseCode} onChange={(v) => updateSchoolInfo("udiseCode", v)} readOnly={!!activeSchool} />
          <TextField testId="input-district" label="District" required value={schoolInfo.district} onChange={(v) => updateSchoolInfo("district", v)} readOnly={!!activeSchool} />
          <TextField testId="input-block" label="Block" required value={schoolInfo.block} onChange={(v) => updateSchoolInfo("block", v)} readOnly={!!activeSchool} />
          <TextField testId="input-village" label="Village" value={schoolInfo.village} onChange={(v) => updateSchoolInfo("village", v)} readOnly={!!activeSchool} />
          <TextField testId="input-fy" label="Financial Year" required value={schoolInfo.financialYear} onChange={(v) => updateSchoolInfo("financialYear", v)} />
          <SelectField
            testId="select-quarter"
            label="Quarter Selection"
            value={schoolInfo.quarter}
            onChange={(v) => handleQuarterChange(v)}
            options={Object.entries(QUARTERS).map(([k, q]) => ({ value: k, label: q.label }))}
          />
          <TextField testId="input-report-date" label="Report Date" type="date" value={schoolInfo.reportDate} onChange={(v) => updateSchoolInfo("reportDate", v)} />
          <TextField testId="input-sanction-no" label="Sanction No & Date" placeholder="e.g. 1234/Edn dated 02-Jan-2026" value={schoolInfo.sanctionNoDate} onChange={(v) => updateSchoolInfo("sanctionNoDate", v)} />
        </div>
      </CardSection>

      {/* Step 1B - Sanction & Vendors */}
      <CardSection testId="section-vendor-info">
        <SectionTitle
          step="Step 1B"
          title="Sanction &amp; Vendor Information"
          note="Vendor names and addresses appear on the Odia hand receipts. Sanction details are used in OGFR-23."
        />
        <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <TextField testId="input-sanctioned-amt" label="Sanctioned Amount" type="number" value={schoolInfo.sanctionedAmount} onChange={(v) => updateSchoolInfo("sanctionedAmount", toNumber(v))} />
          <TextField testId="input-sanction-order" label="Sanction Order No." value={schoolInfo.sanctionOrderNo} onChange={(v) => updateSchoolInfo("sanctionOrderNo", v)} />
          <TextField testId="input-sanction-date" label="Sanction Date" type="date" value={schoolInfo.sanctionDate} onChange={(v) => updateSchoolInfo("sanctionDate", v)} />
          <TextField testId="input-bank-name" label="Bank Name" value={schoolInfo.bankName} onChange={(v) => updateSchoolInfo("bankName", v)} />
          <TextField testId="input-bank-acc" label="Bank Account No." value={schoolInfo.bankAccountNo} onChange={(v) => updateSchoolInfo("bankAccountNo", v)} />
          <TextField testId="input-ifsc" label="IFSC Code" value={schoolInfo.ifscCode} onChange={(v) => updateSchoolInfo("ifscCode", v)} />
        </div>
        <div className="divider-dot mb-6" />
        <h4 className="mb-5 font-heading text-lg font-semibold text-ink-900">Vendor Details (for Hand Receipts)</h4>
        <p className="mb-4 text-sm text-ink-700">
          Pick a vendor from your <Link to="/setup" className="font-semibold text-clay underline">Setup</Link> master list — fields auto-fill below and are still editable for one-off changes.
        </p>
        <div className="space-y-6">
          {[
            { prefix: "grocery", label: "Grocery", category: "grocery" },
            { prefix: "vegetable", label: "Vegetable", category: "vegetable" },
            { prefix: "egg", label: "Egg", category: "egg" },
            { prefix: "fuel", label: "Fuel", category: "fuel" },
            { prefix: "ladoo", label: "Ladoo", category: "ladoo" },
          ].map(({ prefix, label, category }) => (
            <div key={prefix} className="rounded-lg border border-border/70 bg-sand-100/60 p-5" data-testid={`vendor-block-${prefix}`}>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">{label} Vendor</p>
                <div className="ml-auto min-w-[240px] max-w-xs flex-1">
                  <VendorPicker
                    vendors={vendors}
                    category={category}
                    onPick={(v) => applyVendorPick && applyVendorPick(prefix, v)}
                    testId={`vendor-picker-${prefix}`}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <TextField testId={`input-${prefix}-vendor`} label="Vendor Name" value={vendorInfo[`${prefix}Vendor`]} onChange={(v) => updateVendorInfo(`${prefix}Vendor`, v)} />
                <TextField testId={`input-${prefix}-father`} label={`${(vendorInfo[`${prefix}Relation`] === "husband") ? "Husband" : "Father"}'s Name`} value={vendorInfo[`${prefix}Father`]} onChange={(v) => updateVendorInfo(`${prefix}Father`, v)} />
                <TextField testId={`input-${prefix}-village`} label="Village" value={vendorInfo[`${prefix}Village`]} onChange={(v) => updateVendorInfo(`${prefix}Village`, v)} />
                <TextField testId={`input-${prefix}-address`} label="Address" value={vendorInfo[`${prefix}Address`]} onChange={(v) => updateVendorInfo(`${prefix}Address`, v)} />
              </div>
            </div>
          ))}
        </div>
      </CardSection>

      {/* Step 2 - Monthly Data Entry */}
      <CardSection testId="section-monthly-data">
        <SectionTitle
          step="Step 2"
          title="Monthly PM POSHAN Data Entry"
          note="Enter Month-1, Month-2, and Month-3. Closing balances are automatically carried forward."
        />

        <Tabs
          value={String(activeMonthIndex)}
          onValueChange={(val) => setActiveMonthIndex(Number(val))}
        >
          <TabsList className="grid w-full grid-cols-3 bg-sand-100 p-1">
            {months.map((month, index) => (
              <TabsTrigger
                key={month.monthName}
                value={String(index)}
                data-testid={`month-tab-${index}`}
                className="data-[state=active]:bg-clay data-[state=active]:text-white text-ink-900 font-semibold"
              >
                <div className="flex flex-col items-center leading-tight py-1">
                  <span className="text-xs uppercase tracking-[0.18em] opacity-80">Month {index + 1}</span>
                  <span className="text-base">{month.monthName}</span>
                </div>
              </TabsTrigger>
            ))}
          </TabsList>

          {months.map((_, idx) => (
            <TabsContent key={idx} value={String(idx)} className="mt-8 space-y-10" />
          ))}
        </Tabs>

        <div className="mt-8 space-y-10">
          {/* A. Meals */}
          <div data-testid="meals-section">
            <h3 className="mb-4 font-heading text-xl font-semibold text-ink-900">A. Meal Details — {activeMonth.monthName}</h3>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <TextField testId="meals-bv-enroll" label="Bal Vatika Enrollment" type="number" value={activeMonth.meals.balVatikaEnrollment} onChange={(v) => updateMeal(activeMonthIndex, "balVatikaEnrollment", toNumber(v))} />
              <TextField testId="meals-primary-enroll" label="Primary Enrollment" type="number" value={activeMonth.meals.primaryEnrollment} onChange={(v) => updateMeal(activeMonthIndex, "primaryEnrollment", toNumber(v))} />
              <TextField testId="meals-up-enroll" label="Upper Primary Enrollment" type="number" value={activeMonth.meals.upperPrimaryEnrollment} onChange={(v) => updateMeal(activeMonthIndex, "upperPrimaryEnrollment", toNumber(v))} />
              <TextField testId="meals-working-days" label="Working Days" type="number" value={activeMonth.meals.workingDays} onChange={(v) => updateMeal(activeMonthIndex, "workingDays", toNumber(v))} />
              <TextField testId="meals-mdm-days" label="MDM Served Days" type="number" value={activeMonth.meals.mdmServedDays} onChange={(v) => updateMeal(activeMonthIndex, "mdmServedDays", toNumber(v))} />
              <TextField testId="meals-total-served" label="Total Meals Served" type="number" value={activeMonth.meals.totalMealsServed} onChange={(v) => updateMeal(activeMonthIndex, "totalMealsServed", toNumber(v))} />
            </div>
            <p className="mt-4 rounded-lg bg-moss/10 px-4 py-3 text-sm text-ink-700">
              Suggested meals = enrollment × MDM served days = <b className="text-moss-dark">{qty(expectedMeals)}</b>. Use the official register value if it differs.
            </p>
            <h4 className="mt-5 mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">
              Classwise Meals {classwiseMealsFromDaily ? "(from Daily Entry attendance)" : "(auto-calculated from enrollment × MDM days)"}
            </h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField label="Bal Vatika Meals" type="number" readOnly value={(classwiseMealsFromDaily?.[0]?.meals ?? activeClassMeals[0]?.meals) || 0} onChange={() => {}} testId="classwise-meals-bv" />
              <TextField label="Primary Meals" type="number" readOnly value={(classwiseMealsFromDaily?.[1]?.meals ?? activeClassMeals[1]?.meals) || 0} onChange={() => {}} testId="classwise-meals-primary" />
              <TextField label="Upper Primary Meals" type="number" readOnly value={(classwiseMealsFromDaily?.[2]?.meals ?? activeClassMeals[2]?.meals) || 0} onChange={() => {}} testId="classwise-meals-up" />
            </div>
          </div>

          {/* B. Fund Details */}
          <div data-testid="funds-section">
            <h3 className="mb-4 font-heading text-xl font-semibold text-ink-900">B. Fund Details — Cooking Cost</h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <thead className="bg-sand-100 text-left text-ink-700">
                  <tr>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Component</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Opening</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Received</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Expenditure</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {FUND_COMPONENTS.map((fund) => {
                    const opening = getFundOpening(months, activeMonthIndex, fund.key);
                    const closing = getFundClosing(months, activeMonthIndex, fund.key);
                    return (
                      <tr key={fund.key} className="border-t border-border">
                        <th className="p-3 text-left font-semibold text-ink-900">{fund.label}</th>
                        <td className="p-2"><input data-testid={`fund-${fund.key}-opening`} className="table-input" type="number" value={opening} disabled={activeMonthIndex > 0} onChange={(e) => updateFund(activeMonthIndex, fund.key, "opening", toNumber(e.target.value))} /></td>
                        <td className="p-2"><input data-testid={`fund-${fund.key}-received`} className="table-input" type="number" value={activeMonth.funds[fund.key].received} onChange={(e) => updateFund(activeMonthIndex, fund.key, "received", toNumber(e.target.value))} /></td>
                        <td className="p-2"><input data-testid={`fund-${fund.key}-expenditure`} className="table-input" type="number" value={activeMonth.funds[fund.key].expenditure} onChange={(e) => updateFund(activeMonthIndex, fund.key, "expenditure", toNumber(e.target.value))} /></td>
                        <td className={`p-3 font-semibold ${closing < 0 ? "text-destructive" : "text-moss-dark"}`}>{money(closing)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {activeMonthIndex > 0 && (
              <p className="mt-3 text-sm text-ink-700">Opening balance is carried forward from previous month closing balance.</p>
            )}
            {activeMonth?._fundsByClassFromDaily && (
              <div className="mt-3 rounded-lg border border-moss/30 bg-moss/5 px-4 py-3 text-sm" data-testid="funds-from-daily-banner">
                <p className="font-semibold text-moss-dark">From Daily Entry — Cooking Cost expenditure for {activeMonth.monthName}:</p>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-ink-900 sm:grid-cols-4">
                  <span>Bal Vatika: <b>{money(activeMonth._fundsByClassFromDaily.balVatika)}</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="funds-dr-balVatika">of which Dry Ration Rs {(activeMonth._drFundsByClass?.balVatika || 0).toFixed(2)} / {(activeMonth._drRiceByClass?.balVatika || 0).toFixed(3)} kg</span>}</span>
                  <span>Primary: <b>{money(activeMonth._fundsByClassFromDaily.primary)}</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="funds-dr-primary">of which Dry Ration Rs {(activeMonth._drFundsByClass?.primary || 0).toFixed(2)} / {(activeMonth._drRiceByClass?.primary || 0).toFixed(3)} kg</span>}</span>
                  <span>Upper Primary: <b>{money(activeMonth._fundsByClassFromDaily.upperPrimary)}</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="funds-dr-upperPrimary">of which Dry Ration Rs {(activeMonth._drFundsByClass?.upperPrimary || 0).toFixed(2)} / {(activeMonth._drRiceByClass?.upperPrimary || 0).toFixed(3)} kg</span>}</span>
                  <span>High School: <b>{money(activeMonth._fundsByClassFromDaily.highSchool)}</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="funds-dr-highSchool">of which Dry Ration Rs {(activeMonth._drFundsByClass?.highSchool || 0).toFixed(2)} / {(activeMonth._drRiceByClass?.highSchool || 0).toFixed(3)} kg</span>}</span>
                </div>
                <p className="mt-2 text-xs text-ink-700">Use these as your monthly cooking cost expenditure values above.</p>
              </div>
            )}
          </div>

          {/* C. Grains */}
          <div data-testid="grains-section">
            <h3 className="mb-4 font-heading text-xl font-semibold text-ink-900">C. Food Grain Details</h3>
            {activeMonth?._riceByClassFromDaily && (
              <div className="mb-3 rounded-lg border border-moss/30 bg-moss/5 px-4 py-3 text-sm" data-testid="rice-from-daily-banner">
                <p className="font-semibold text-moss-dark">From Daily Entry — Rice consumption for {activeMonth.monthName}:</p>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-ink-900 sm:grid-cols-4">
                  <span>Bal Vatika: <b>{qty(activeMonth._riceByClassFromDaily.balVatika)} kg</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="rice-dr-balVatika">of which Dry Ration {(activeMonth._drRiceByClass?.balVatika || 0).toFixed(3)} kg</span>}</span>
                  <span>Primary: <b>{qty(activeMonth._riceByClassFromDaily.primary)} kg</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="rice-dr-primary">of which Dry Ration {(activeMonth._drRiceByClass?.primary || 0).toFixed(3)} kg</span>}</span>
                  <span>Upper Primary: <b>{qty(activeMonth._riceByClassFromDaily.upperPrimary)} kg</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="rice-dr-upperPrimary">of which Dry Ration {(activeMonth._drRiceByClass?.upperPrimary || 0).toFixed(3)} kg</span>}</span>
                  <span>High School: <b>{qty(activeMonth._riceByClassFromDaily.highSchool)} kg</b>{activeMonth._drDays > 0 && <span className="block text-xs text-clay-dark" data-testid="rice-dr-highSchool">of which Dry Ration {(activeMonth._drRiceByClass?.highSchool || 0).toFixed(3)} kg</span>}</span>
                </div>
              </div>
            )}
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[820px] border-collapse text-sm">
                <thead className="bg-sand-100 text-left text-ink-700">
                  <tr>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Category</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Item</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Opening</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Received</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Consumption</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {GRAIN_ITEMS.map((grain) => {
                    const opening = getGrainOpening(months, activeMonthIndex, grain.key);
                    const closing = getGrainClosing(months, activeMonthIndex, grain.key);
                    return (
                      <tr key={grain.key} className="border-t border-border">
                        <th className="p-3 text-left font-semibold text-ink-900">{grain.category}</th>
                        <td className="p-3 text-ink-900">{grain.item}</td>
                        <td className="p-2"><input data-testid={`grain-${grain.key}-opening`} className="table-input" type="number" value={opening} disabled={activeMonthIndex > 0} onChange={(e) => updateGrain(activeMonthIndex, grain.key, "opening", toNumber(e.target.value))} /></td>
                        <td className="p-2"><input data-testid={`grain-${grain.key}-received`} className="table-input" type="number" value={activeMonth.grains[grain.key].received} onChange={(e) => updateGrain(activeMonthIndex, grain.key, "received", toNumber(e.target.value))} /></td>
                        <td className="p-2"><input data-testid={`grain-${grain.key}-consumption`} className="table-input" type="number" value={activeMonth.grains[grain.key].consumption} onChange={(e) => updateGrain(activeMonthIndex, grain.key, "consumption", toNumber(e.target.value))} /></td>
                        <td className={`p-3 font-semibold ${closing < 0 ? "text-destructive" : "text-moss-dark"}`}>{qty(closing)} kg</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* D. Ingredients */}
          <div data-testid="ingredients-section">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-heading text-xl font-semibold text-ink-900">D. Ingredient Expenditure</h3>
                {activeIsFromDaily && (
                  <p className="mt-1 text-xs font-medium text-moss-dark">
                    Auto-aggregated from {activeMonth._dailyEntryCount} daily entries · classwise split below
                  </p>
                )}
              </div>
              {activeIsFromDaily ? (
                <Link
                  to="/daily"
                  data-testid="open-daily-entry-link"
                  className="inline-flex items-center gap-2 rounded-md border border-clay bg-white px-3 py-1.5 text-sm font-semibold text-clay transition hover:bg-clay hover:text-white"
                >
                  <CalendarDays className="h-3.5 w-3.5" /> Edit in Daily Entry
                </Link>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addIngredient(activeMonthIndex)}
                  data-testid="add-ingredient-btn"
                  className="gap-2"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Custom Ingredient
                </Button>
              )}
            </div>
            {!activeIsFromDaily && cloudId && (
              <p className="mb-3 rounded-lg bg-sand-100 px-4 py-2.5 text-sm text-ink-700">
                Tip: Add Daily Entries for {activeMonth.monthName} to auto-fill this table from real day-by-day records.
              </p>
            )}
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[860px] border-collapse text-sm">
                <thead className="bg-sand-100 text-left text-ink-700">
                  <tr>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Ingredient</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Quantity</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Unit</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">{activeIsFromDaily ? "Avg Rate" : "Rate"}</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeMonth.ingredients.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-ink-700">
                      {activeIsFromDaily ? "No ingredients recorded for this month yet." : "No ingredients yet."}
                    </td></tr>
                  )}
                  {activeMonth.ingredients.map((ingredient, index) => (
                    <tr key={ingredient.id} className={`border-t border-border ${ingredient.isOverride ? "bg-ochre/5" : ""}`}>
                      <td className="p-2">
                        {activeIsFromDaily ? (
                          <div className="flex items-center gap-2 px-2 py-1 font-medium text-ink-900">
                            <span>{displayIngredientName(ingredient)}</span>
                            {ingredient.isOverride && (
                              <span data-testid={`ing-override-badge-${ingredient.name}`} className="rounded-full bg-ochre/20 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ochre">
                                Override
                              </span>
                            )}
                          </div>
                        ) : (
                          <input className="table-input min-w-[260px]" value={displayIngredientName(ingredient)} onChange={(e) => updateIngredient(activeMonthIndex, index, "name", e.target.value)} />
                        )}
                      </td>
                      <td className="p-2">
                        {activeIsFromDaily ? (
                          <div className="px-2 py-1 text-ink-900 font-mono">{qty(ingredient.quantity)}</div>
                        ) : (
                          <input data-testid={`ing-${ingredient.id}-qty`} className="table-input" type="number" value={ingredient.quantity} onChange={(e) => updateIngredient(activeMonthIndex, index, "quantity", toNumber(e.target.value))} />
                        )}
                      </td>
                      <td className="p-2">
                        {activeIsFromDaily ? (
                          <div className="px-2 py-1 text-ink-700">{ingredient.unit}</div>
                        ) : (
                          <input className="table-input" value={ingredient.unit} onChange={(e) => updateIngredient(activeMonthIndex, index, "unit", e.target.value)} />
                        )}
                      </td>
                      <td className="p-2">
                        {activeIsFromDaily ? (
                          <div className="px-2 py-1 text-ink-700">{money(ingredient.rate)}</div>
                        ) : (
                          <input data-testid={`ing-${ingredient.id}-rate`} className="table-input" type="number" value={ingredient.rate} onChange={(e) => updateIngredient(activeMonthIndex, index, "rate", toNumber(e.target.value))} />
                        )}
                      </td>
                      <td className="p-3 font-semibold text-clay">{money(ingredientAmount(ingredient))}</td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`ing-edit-btn-${ingredient.name.replace(/\s+/g, "-")}`}
                            onClick={() => openEditIngredient(ingredient)}
                            className="text-ink-700 hover:text-clay"
                            title="Edit quantity / rate"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {activeIsFromDaily && ingredient.isOverride && clearOverride && (
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`ing-reset-btn-${ingredient.name.replace(/\s+/g, "-")}`}
                              onClick={() => clearOverride(activeMonthIndex, ingredient.name)}
                              className="text-ochre hover:text-ochre"
                              title="Reset to daily-derived value"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {!activeIsFromDaily && ingredient.isCustom && (
                            <Button size="sm" variant="ghost" onClick={() => removeIngredient(activeMonthIndex, index)} className="text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-sand-100">
                    <th colSpan={4} className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">Monthly Ingredient Total</th>
                    <th className="p-3 text-left text-clay">{money(activeMonthSummary.ingredientTotal)}</th>
                    <th />
                  </tr>
                  {(() => {
                    const cookingCost =
                      (activeMonth.funds?.balVatika?.expenditure || 0) +
                      (activeMonth.funds?.primary?.expenditure || 0) +
                      (activeMonth.funds?.upperPrimary?.expenditure || 0) -
                      // Ingredients are cooked-meal purchases: compare with the MDM part only.
                      ["balVatika", "primary", "upperPrimary"].reduce((t, k) => t + (Number(activeMonth._drFundsByClass?.[k]) || 0), 0);
                    const delta = Math.abs(cookingCost - (activeMonthSummary.ingredientTotal || 0));
                    const isMatch = delta < 0.01;
                    return (
                      <tr className="border-t-2 border-clay bg-clay/5" data-testid="ing-grand-total-row">
                        <th colSpan={4} className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-clay-dark">
                          Grand Total <span className="normal-case font-normal text-ink-700">(= Total Cooking Cost for {activeMonth.monthName}{activeMonth._drDays > 0 ? ", excluding Dry Ration" : ""})</span>
                        </th>
                        <th className="p-3 text-left font-heading text-base text-clay-dark" data-testid="ing-grand-total-value">
                          {money(cookingCost)}
                          {!isMatch && (
                            <span className="ml-2 rounded-full bg-destructive/15 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-destructive">
                              Δ {money(delta)}
                            </span>
                          )}
                        </th>
                        <th />
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
            {activeIsFromDaily && activeMonth._classwiseFromDaily && activeMonth._classwiseFromDaily.size > 0 && (
              <div className="mt-6">
                <h4 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">
                  Classwise Breakdown — {activeMonth.monthName}
                </h4>
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full min-w-[760px] border-collapse text-sm">
                    <thead className="bg-sand-100 text-left text-ink-700">
                      <tr>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]" rowSpan={2}>Ingredient</th>
                        <th className="p-3 text-center text-xs font-semibold uppercase tracking-[0.16em]" colSpan={2}>Bal Vatika</th>
                        <th className="p-3 text-center text-xs font-semibold uppercase tracking-[0.16em]" colSpan={2}>Primary</th>
                        <th className="p-3 text-center text-xs font-semibold uppercase tracking-[0.16em]" colSpan={2}>Upper Primary</th>
                      </tr>
                      <tr>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Qty</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Qty</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Qty</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(activeMonth._classwiseFromDaily.entries()).map(([name, cw]) => (
                        <tr key={`cw-${name}`} className="border-t border-border">
                          <td className="p-3 font-medium text-ink-900">{name}</td>
                          <td className="p-3 text-ink-700">{qty(cw.qtyBalVatika)}</td>
                          <td className="p-3 font-semibold text-clay">{money(cw.balVatika)}</td>
                          <td className="p-3 text-ink-700">{qty(cw.qtyPrimary)}</td>
                          <td className="p-3 font-semibold text-clay">{money(cw.primary)}</td>
                          <td className="p-3 text-ink-700">{qty(cw.qtyUpperPrimary)}</td>
                          <td className="p-3 font-semibold text-clay">{money(cw.upperPrimary)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* E. Cooks */}
          <div data-testid="cooks-section">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="font-heading text-xl font-semibold text-ink-900">E. Cook Cum Helper Payments</h3>
              <Button size="sm" variant="outline" onClick={() => addCook(activeMonthIndex)} data-testid="add-cook-btn" className="gap-2">
                <Plus className="h-3.5 w-3.5" /> Add Cook / Helper
              </Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[680px] border-collapse text-sm">
                <thead className="bg-sand-100 text-left text-ink-700">
                  <tr>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Name</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Mode</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeMonth.cooks.map((cook, index) => (
                    <tr key={cook.id} className="border-t border-border">
                      <td className="p-2">
                        <div className="flex flex-col gap-1.5">
                          <input className="table-input" value={cook.name} placeholder={`Cook ${index + 1}`} onChange={(e) => updateCook(activeMonthIndex, index, "name", e.target.value)} />
                          <CchPicker
                            cchs={cchs}
                            value={cook.cchId || ""}
                            onPick={(c) => applyCookPick && applyCookPick(activeMonthIndex, index, c)}
                            testId={`cook-picker-${index}`}
                          />
                        </div>
                      </td>
                      <td className="p-2"><input data-testid={`cook-${cook.id}-amt`} className="table-input" type="number" value={cook.amount} onChange={(e) => updateCook(activeMonthIndex, index, "amount", toNumber(e.target.value))} /></td>
                      <td className="p-2">
                        <select className="table-input" value={cook.mode} onChange={(e) => updateCook(activeMonthIndex, index, "mode", e.target.value)}>
                          <option>Bank</option><option>Cash</option><option>UPI</option><option>Other</option>
                        </select>
                      </td>
                      <td className="p-2">
                        <Button size="sm" variant="ghost" onClick={() => removeCook(activeMonthIndex, index)} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-sand-100">
                    <th className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">Monthly Cook Payment Total</th>
                    <th className="p-3 text-left text-clay">{money(activeMonthSummary.cookTotal)}</th>
                    <th colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </CardSection>

      {/* Edit Ingredient Dialog (D. Ingredient Expenditure → Edit action) */}
      <Dialog open={editIngOpen} onOpenChange={(o) => { setEditIngOpen(o); if (!o) setEditIngDraft(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Ingredient — {editIngDraft?.name}</DialogTitle>
            <DialogDescription>
              {activeIsFromDaily
                ? "Override the daily-aggregated value for this month. Use the Reset button later to restore the daily-derived total."
                : "Update the quantity or rate for this ingredient row."}
            </DialogDescription>
          </DialogHeader>
          {editIngDraft && (
            <div className="grid gap-4 py-2 sm:grid-cols-2">
              <TextField label="Quantity" type="number" testId="ing-edit-qty" value={editIngDraft.quantity} onChange={(v) => setEditIngDraft((d) => ({ ...d, quantity: toNumber(v) }))} />
              <TextField label="Unit" value={editIngDraft.unit} readOnly onChange={() => {}} />
              <TextField label="Rate" type="number" testId="ing-edit-rate" value={editIngDraft.rate} onChange={(v) => setEditIngDraft((d) => ({ ...d, rate: toNumber(v) }))} />
              <div>
                <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Amount</span>
                <div className="field-input bg-sand-100 text-clay font-semibold">
                  {money((Number(editIngDraft.quantity) || 0) * (Number(editIngDraft.rate) || 0))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIngOpen(false)}>Cancel</Button>
            <Button
              data-testid="ing-edit-save"
              onClick={() => {
                if (!editIngDraft) return;
                if (activeIsFromDaily && editIngredient) {
                  editIngredient(activeMonthIndex, editIngDraft.name, { quantity: editIngDraft.quantity, rate: editIngDraft.rate });
                } else if (updateIngredient) {
                  // Find the index in current month's ingredients by name
                  const idx = activeMonth.ingredients.findIndex((i) => i.name === editIngDraft.name);
                  if (idx >= 0) {
                    updateIngredient(activeMonthIndex, idx, "quantity", editIngDraft.quantity);
                    updateIngredient(activeMonthIndex, idx, "rate", editIngDraft.rate);
                  }
                }
                setEditIngOpen(false);
                setEditIngDraft(null);
              }}
              className="bg-clay text-white hover:bg-clay-dark"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
